import React, { useEffect, useRef, useState } from "react";
import "./TopProgressBar1.scss";

interface FlowItem {
  id: number | string;
  name: string;
  childs?: FlowItem[];
}

interface Props {
  tree: FlowItem[];
  currentIndex: number;
  currentChildIndex: number;
  currentGrandChildIndex: number;
  completed: (number | string)[];
  isItTop?: boolean;
}

const TopProgressBar1: React.FC<Props> = ({
  tree,
  currentIndex,
  currentChildIndex,
  currentGrandChildIndex,
  completed,
  isItTop,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const [fillWidth, setFillWidth] = useState(0);

  const currentMain = tree[currentIndex];
  const currentChild =
    currentMain?.childs?.[currentChildIndex];

  const currentMainId = currentMain?.id;
  const currentChildId = currentChild?.id;
  const currentGrandId =
    currentChild?.childs?.[
      currentGrandChildIndex >= 0 ? currentGrandChildIndex : 0
    ]?.id;

  const currentId =
    currentGrandId ?? currentChildId ?? currentMainId;

  const visibleSteps: { id: number | string; name: string; level: number }[] = [];

  tree.forEach((main, mainIndex) => {
    visibleSteps.push({ id: main.id, name: main.name, level: 0 });

    if (mainIndex === currentIndex && main.childs?.length) {
      const childStart = Math.max(0, currentChildIndex - 1);
      const childEnd = Math.min(main.childs.length, childStart + 3);

      for (let i = childStart; i < childEnd; i++) {
        const child = main.childs[i];
        visibleSteps.push({ id: child.id, name: child.name, level: 1 });

        if (i === currentChildIndex && child.childs?.length) {
          const gIndex = currentGrandChildIndex >= 0 ? currentGrandChildIndex : 0;
          const grandStart = Math.max(0, gIndex - 1);
          const grandEnd = Math.min(child.childs.length, grandStart + 3);

          for (let g = grandStart; g < grandEnd; g++) {
            visibleSteps.push({
              id: child.childs[g].id,
              name: child.childs[g].name,
              level: 2,
            });
          }
        }
      }
    }
  });

  useEffect(() => {
    if (!wrapperRef.current || !lineRef.current || !currentId) return;

    const currentElement = wrapperRef.current.querySelector(
      `[data-step-id="${String(currentId)}"]`
    ) as HTMLElement | null;

    if (!currentElement) return;

    const circle = currentElement.querySelector(".phf-circle") as HTMLElement | null;
    if (!circle) return;

    const lineRect = lineRef.current.getBoundingClientRect();
    const circleRect = circle.getBoundingClientRect();
    const centerX = circleRect.left + circleRect.width / 2;

    setFillWidth(centerX - lineRect.left);
  }, [currentId, visibleSteps.length]);

  return (
    <div
      ref={wrapperRef}
      className={`phf-wrapper ${isItTop ? "top-phf-wrapper" : ""}`}
    >
      <div ref={lineRef} className="phf-line-bg">
        <div className="phf-line-fill" style={{ width: `${fillWidth}px` }} />
      </div>

      {visibleSteps.map((step) => {
        const isCompleted = completed.includes(step.id);

        const isCurrentPath =
          step.id === currentMainId ||
          step.id === currentChildId ||
          step.id === currentGrandId;

        const statusClass = isCompleted
          ? "phf-completed"
          : isCurrentPath
          ? "phf-current"
          : "phf-pending";

        return (
          <div
            key={`${step.level}-${String(step.id)}`}
            data-step-id={String(step.id)}
            className={`phf-item phf-level-${step.level} ${statusClass}`}
          >
            <div className="phf-circle" />
            <div className="phf-label">{step.name}</div>
          </div>
        );
      })}
    </div>
  );
};

export default TopProgressBar1;